// src/pages/CreateTaskPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
// ADD this import
import { useNavigate, useLocation } from 'react-router-dom';
import {
      collection, addDoc, serverTimestamp, getDocs, query, orderBy, where,
      writeBatch, doc, Timestamp, getDoc
    } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiPlusCircle } from 'react-icons/fi';
import styles from '../styles/CreateTaskPage.module.css';

// Roles
const managerRoles = ['Manager', 'Accounts Manager'];
const TRAINEE_ROLE_STRING = 'Trainee';

// --- Helpers to robustly detect "fired/inactive" ---
const toLower = (v) => (typeof v === 'string' ? v.toLowerCase() : '');
const toDateObj = (v) => {
  if (!v) return null;
  if (v?.toDate) {
    try { return v.toDate(); } catch {}
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const isFiredOrInactive = (user) => {
  const inactiveFlag = user?.active === false || user?.isActive === false;
  const firedFlag = user?.isFired === true;

  const statusStr = toLower(user?.employmentStatus || user?.status || '');
  const statusFlag = ['fired', 'terminated', 'inactive', 'disabled'].includes(statusStr);

  const endDate = toDateObj(user?.employmentEndDate);
  const endDateFlag = endDate ? endDate.getTime() <= Date.now() : false;

  return inactiveFlag || firedFlag || statusFlag || endDateFlag;
};

const CreateTaskPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // --- Form State ---
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [assignedTo, setAssignedTo] = useState([]); // Array of UIDs
  const [priority, setPriority] = useState('Medium');
  const [selectedClientIds, setSelectedClientIds] = useState([]); // Array of client IDs

  // --- Helper State ---
  const [employees, setEmployees] = useState([]);       // assignable (excludes trainees + fired/inactive)
  const [clients, setClients] = useState([]);           // { id, name }
  const [managerUids, setManagerUids] = useState([]);   // UIDs of managers
  const [loading, setLoading] = useState(false);        // submit loading
  const [formLoading, setFormLoading] = useState(true); // initial data
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');

  const isMounted = useRef(true);

  const priorityOptions = ['Low', 'Medium', 'High'];

  // Build quick maps for display
  const employeeById = useMemo(() => {
    const m = new Map();
    employees.forEach(e => m.set(e.id, e));
    return m;
  }, [employees]);

  useEffect(() => {
    isMounted.current = true;
  
    const fetchInitialData = async () => {
      if (!isMounted.current) return;
      setFormLoading(true);
      setError('');
      try {
        // ---- Users ----
        const usersCol = collection(db, 'users');
        const qAll = query(usersCol, orderBy('name', 'asc'));
        const allUsersSnapshot = await getDocs(qAll);
        if (!isMounted.current) return;
  
        const allUsersList = allUsersSnapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        }));
  
        // ✅ Exclude trainees + fired/inactive
        const employeeList = allUsersList
          .filter(u =>
            u.role !== TRAINEE_ROLE_STRING &&
            !(u.isFired === true ||
              u.active === false ||
              u.isActive === false ||
              String(u.employmentStatus || u.status || '').toLowerCase() === 'fired' ||
              (u.employmentEndDate && new Date(u.employmentEndDate) <= new Date())
            )
          )
          .map(u => ({
            id: u.id,
            name: u.name || u.email || u.id,
            email: u.email || '',
            whatsappNumber: u.whatsappNumber || '',
          }));
        setEmployees(employeeList);
  
        // Managers
        const managerIdList = allUsersList
          .filter(u => managerRoles.includes(u.role))
          .map(u => u.id);
        setManagerUids(managerIdList);
  
        // ---- Clients ----
        const clientsRef = collection(db, 'clients');
        const qClients = query(clientsRef, orderBy('name', 'asc'));
        const clientsSnapshot = await getDocs(qClients);
        if (!isMounted.current) return;
  
        const clientList = clientsSnapshot.docs.map(d => ({
          id: d.id,
          name: d.data().name,
        }));
        setClients(clientList);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        if (isMounted.current) {
          setError(prev => prev || 'Could not load dropdown data.');
          setEmployees([]);
          setManagerUids([]);
          setClients([]);
        }
      } finally {
        if (isMounted.current) setFormLoading(false);
      }
    };
  
    fetchInitialData();
    return () => { isMounted.current = false; };
  }, []);
  

  // Filtered dropdown lists
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clients;
    return clients.filter(c =>
      (c.name || '').toLowerCase().includes(clientSearchTerm.toLowerCase())
    );
  }, [clients, clientSearchTerm]);

  const filteredEmployees = useMemo(() => {
    if (!assigneeSearchTerm) return employees;
    return employees.filter(emp =>
      (emp.name || '').toLowerCase().includes(assigneeSearchTerm.toLowerCase())
    );
  }, [employees, assigneeSearchTerm]);

  // Multi-select handlers
  const handleAssigneeChange = (e) => {
    const { options } = e.target;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) selected.push(options[i].value);
    }
    setAssignedTo(selected);
  };

  const handleClientChange = (e) => {
    const { options } = e.target;
    const ids = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) ids.push(options[i].value);
    }
    setSelectedClientIds(ids);
  };

  const getQueryParams = () => {
    const p = new URLSearchParams(location.search);
    const assigneeParam = (p.get('assignee') || '').trim(); // supports single or comma-separated
    const titleParam = (p.get('title') || '').trim();
    const assigneesFromQuery = assigneeParam
      ? assigneeParam.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    return { assigneesFromQuery, titleFromQuery: titleParam };
  };
  

  // ---------- Vacation Conflict Check ----------
  const checkVacationConflicts = async (assigneeUids, rangeStartStr, rangeEndStr) => {
    // returns: { conflicts: Array<{ userId, date, type, note }>, byUser: Map }
    const conflicts = [];
    for (const uid of assigneeUids) {
      // Query attendance where this user has Vacation within range
      const qVac = query(
        collection(db, 'attendance'),
        where('userId', '==', uid),
        where('status', '==', 'Vacation'),
        where('date', '>=', rangeStartStr),
        where('date', '<=', rangeEndStr)
      );
      const snap = await getDocs(qVac);
      snap.forEach(docSnap => {
        const data = docSnap.data();
        conflicts.push({
          userId: uid,
          date: data.date,
          type: data?.vacation?.type || '',
          note: data?.vacation?.note || ''
        });
      });
    }
    // Grouping map for message
    const byUser = new Map();
    for (const c of conflicts) {
      if (!byUser.has(c.userId)) byUser.set(c.userId, []);
      byUser.get(c.userId).push(c);
    }
    // Sort dates in message
    byUser.forEach(arr => arr.sort((a, b) => a.date.localeCompare(b.date)));
    return { conflicts, byUser };
  };

  // Check a single day by known attendance doc id: attendance/{uid}_{YYYY-MM-DD}
const checkVacationForDayById = async (uid, ymd) => {
    if (!uid || !ymd) return false;
    const attendanceRef = doc(db, 'attendance', `${uid}_${ymd}`);
    const snap = await getDoc(attendanceRef);
    if (!snap.exists()) return false;
    const d = snap.data();
    return d?.status === 'Vacation';
  };
  

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // ── Validation ────────────────────────────────────────────────────────────────
    if (!currentUser) { setError("Authentication error."); return; }
    if (!taskName.trim()) { setError("Task Name is required."); return; }
    if (selectedClientIds.length === 0) { setError("Please select at least one Client."); return; }
    if (!endDate) { setError("End Date is required."); return; }
    if (startDate && new Date(startDate) > new Date(endDate)) {
      setError("End date cannot be before start date.");
      return;
    }
    if (assignedTo.length === 0) {
      setError("Please assign the task to at least one person.");
      return;
    }
  
    // ── Local helpers (no indexes needed) ─────────────────────────────────────────
    const pad2 = (n) => String(n).padStart(2, '0');
    const ymdFromDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const listDateRangeYMD = (startYmd, endYmd) => {
      const start = new Date(startYmd + 'T00:00:00');
      const end   = new Date(endYmd   + 'T00:00:00');
      const out = [];
      for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
        out.push(ymdFromDate(dt));
      }
      return out;
    };
    // Read attendance docs by known IDs: attendance/{uid}_{YYYY-MM-DD}
    const checkVacationsForAssignees = async (uids, startYmd, endYmd) => {
      const days = listDateRangeYMD(startYmd, endYmd);
      const conflicts = [];
      for (const uid of uids) {
        for (const day of days) {
          const ref = doc(db, 'attendance', `${uid}_${day}`);
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDoc(ref);
          if (snap.exists() && snap.data()?.status === 'Vacation') {
            const data = snap.data();
            conflicts.push({
              uid,
              date: day,
              type: data?.vacation?.type || '',
              note: data?.vacation?.note || ''
            });
          }
        }
      }
      return conflicts;
    };
  
    setError('');
    setLoading(true);
    setSuccessMessage('');
    let createdTaskId = null;
    let taskCreationFailed = false;
  
    // ── Vacation pre-check (warn & confirm) ───────────────────────────────────────
    try {
      const startYmd = (startDate && startDate.trim()) ? startDate.trim() : endDate.trim();
      const endYmd   = endDate.trim();
      const conflicts = await checkVacationsForAssignees(assignedTo, startYmd, endYmd);
  
      if (conflicts.length > 0) {
        const nameById = new Map(employees.map(e => [e.id, e.name || e.email || e.id]));
        const lines = conflicts
          .map(c => `${nameById.get(c.uid) || c.uid} — ${c.date}${c.type ? ` (${c.type})` : ''}`)
          .join('\n');
  
        const proceed = window.confirm(
          `⚠️ Vacation conflict detected:\n\n${lines}\n\nDo you want to create the task anyway?`
        );
        if (!proceed) {
          setLoading(false);
          return; // cancelled
        }
      }
    } catch (vacErr) {
      console.warn('Vacation check failed (continuing):', vacErr);
      const proceed = window.confirm(
        "Could not verify vacation status. Do you want to create the task anyway?"
      );
      if (!proceed) {
        setLoading(false);
        return; // cancelled
      }
    }
  
    // ── Prepare payload ───────────────────────────────────────────────────────────
    const selectedClientNames = clients
      .filter(c => selectedClientIds.includes(c.id))
      .map(c => c.name);
  
    const clientNameDisplay =
      selectedClientNames.length > 1
        ? `${selectedClientNames.length} clients`
        : selectedClientNames[0] || 'Unknown Client';
  
    try {
      // visibleTo: creator + assignees + managers
      const visibilitySet = new Set();
      if (currentUser.uid) visibilitySet.add(currentUser.uid);
      assignedTo.forEach(uid => visibilitySet.add(uid));
      managerUids.forEach(uid => visibilitySet.add(uid));
      const visibleToArray = Array.from(visibilitySet);
  
      const tasksCol = collection(db, 'tasks');
      const taskData = {
        taskName: taskName.trim(),
        description: description.trim(),
        startDate: startDate ? Timestamp.fromDate(new Date(startDate + 'T00:00:00')) : null,
        endDate: Timestamp.fromDate(new Date(endDate + 'T00:00:00')),
        status: 'Not Started',
        assignedTo, // array of UIDs
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        priority,
        visibleTo: visibleToArray,
        clientIds: selectedClientIds,
        clientNames: selectedClientNames,
      };
  
      const docRef = await addDoc(tasksCol, taskData);
      createdTaskId = docRef.id;
  
      // ── Notify assignees (in-app) + optional email ─────────────────────────────
      let mailtoLink = null;
      let finalMessage = `Task "${taskData.taskName}" created successfully!`;
  
      if (assignedTo.length > 0) {
        const batch = writeBatch(db);
        const notificationsCol = collection(db, 'notifications');
        const creatorName = currentUser.displayName || currentUser.email || 'Someone';
  
        const assignedUsersData = employees.filter(emp => assignedTo.includes(emp.id));
        const assignedEmails = assignedUsersData
          .filter(emp => emp.email)
          .map(emp => emp.email);
  
        if (assignedEmails.length > 0) {
          const recipientEmails = assignedEmails.join(',');
          const emailSubject = `New Task Assigned: ${taskData.taskName}`;
          const taskUrl = `${window.location.origin}/task/${createdTaskId}`;
          const emailBodyLines = [
            `Hello,`,
            ``,
            `You have been assigned a new task: "${taskData.taskName}"`,
            ``,
            `Client(s): ${clientNameDisplay}`,
            `Priority: ${taskData.priority}`,
            `Due Date: ${new Date(endDate + 'T00:00:00').toLocaleDateString()}`,
            `Description: ${taskData.description || '(No description provided)'}`,
            ``,
            `You can view the full task details here:`,
            taskUrl,
            ``,
            `Thanks,`,
            `${creatorName}`
          ];
          const emailBody = emailBodyLines.join('\n');
          mailtoLink = `mailto:${recipientEmails}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
          finalMessage = `Task "${taskData.taskName}" created! Attempting to open email client...`;
        } else {
          finalMessage = `Task "${taskData.taskName}" created! (No assignee emails for direct notification).`;
        }
  
        assignedTo.forEach(assigneeUid => {
          const notificationRef = doc(notificationsCol);
          const notificationData = {
            userId: assigneeUid,
            taskId: createdTaskId,
            taskName: taskData.taskName,
            message: `${creatorName} assigned you a new task: "${taskData.taskName}" for ${clientNameDisplay}`,
            timestamp: serverTimestamp(),
            isRead: false,
            createdBy: currentUser.uid,
            createdByName: creatorName,
            type: "task_assignment"
          };
          batch.set(notificationRef, notificationData);
        });
  
        await batch.commit();
        // WhatsApp notifications are sent automatically by the onTaskCreatedSendWhatsApp
        // Firestore trigger — no frontend call needed.
      }

      // Try to open email client (non-blocking UX)
      if (mailtoLink) {
        try {
          await new Promise(resolve => setTimeout(resolve, 100));
          window.open(mailtoLink);
        } catch (mailError) {
          console.warn("Could not open mail client:", mailError);
          setError(`Task created, but could not open email client. Please notify assignees manually.`);
          setLoading(false);
          return;
        }
      }
  
      setSuccessMessage(finalMessage);
      // Navigate with a deterministic message (avoid relying on async state)
      setTimeout(() => {
        navigate('/dashboard', {
          state: { message: `Task "${taskData.taskName}" created successfully!` }
        });
      }, 1200);
  
    } catch (err) {
      console.error("Error during task creation/notification/email:", err);
      if (createdTaskId) {
        setError('Task created, but failed during notification/email step.');
      } else {
        setError('Failed to create task.');
        taskCreationFailed = true;
      }
    } finally {
      if (taskCreationFailed) setLoading(false);
    }
  };
  
  return (
    <div className={styles.pageContainer}>
      <div className={styles.formBox}>
        <div className={styles.formHeader}>
          <h2 className={styles.formTitle}>
            <FiPlusCircle className={styles.titleIcon} /> Create New Task
          </h2>
          <p className={styles.formSubtitle}>Assign work to your team and link it to one or more clients.</p>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

        {formLoading ? (
          <div className={styles.loadingIndicator}>Loading form data...</div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label htmlFor="taskName">Task Name</label>
              <input
                type="text"
                id="taskName"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                required
                disabled={loading}
                placeholder="e.g., Client Website Mockup"
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="client">
                Client(s)
                {selectedClientIds.length > 0 && <span className={styles.countTag}> — {selectedClientIds.length} selected</span>}
              </label>
              <input
                type="text"
                placeholder="Search Clients..."
                value={clientSearchTerm}
                onChange={(e) => setClientSearchTerm(e.target.value)}
                disabled={loading || formLoading || clients.length === 0}
                className={styles.searchInput}
              />
              <select
                id="client"
                multiple
                value={selectedClientIds}
                onChange={handleClientChange}
                required
                size="5"
                disabled={loading || formLoading || clients.length === 0}
                className={styles.multiSelectInput}
              >
                {filteredClients.length === 0 && !formLoading && (
                  <option disabled>No matching clients</option>
                )}
                {filteredClients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
              <small className={styles.selectHelpText}>Hold Ctrl/Cmd to select multiple</small>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={5}
                disabled={loading}
                placeholder="Provide details..."
              />
            </div>

            <div className={styles.gridThreeCols}>
              <div className={styles.inputGroup}>
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  disabled={loading}
                  min={startDate || ''}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="priority">Priority</label>
                <select
                  id="priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={loading}
                  className={styles.prioritySelect}
                >
                  {priorityOptions.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="assignedTo">
                Assign To (Employees only)
                {assignedTo.length > 0 && <span className={styles.countTag}> — {assignedTo.length} selected</span>}
              </label>
              <input
                type="text"
                placeholder="Search Employees..."
                value={assigneeSearchTerm}
                onChange={(e) => setAssigneeSearchTerm(e.target.value)}
                disabled={loading || formLoading || employees.length === 0}
                className={styles.searchInput}
              />
              <select
                id="assignedTo"
                multiple
                value={assignedTo}
                onChange={handleAssigneeChange}
                required
                size="6"
                disabled={loading || formLoading || employees.length === 0}
                className={styles.multiSelectInput}
              >
                {filteredEmployees.length === 0 && !formLoading && (
                  <option disabled>No matching employees</option>
                )}
                {filteredEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
              <small className={styles.selectHelpText}>Hold Ctrl/Cmd to select multiple</small>
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={`${styles.formButton} ${styles.cancelButton}`}
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${styles.formButton} ${styles.submitButton}`}
                disabled={loading || formLoading}
              >
                {loading ? <span className={styles.loadingSpinner}></span> : 'Create Task'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateTaskPage;
